'use client';

import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useEffect,
} from 'react';
import { ToastProps, ToastType } from './Toast';
import ToastPortal from './ToastPortal';

interface ToastContextType {
  showToast: (
    message: string,
    type?: ToastType,
    header?: string,
    id?: string,
    persist?: boolean,
    action?: ReactNode
  ) => void;
  dismissToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

let toastId = 0;

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastProps[]>([]);

  const handleClose = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback(
    (
      message: string,
      type: ToastType = 'info',
      header?: string,
      id?: string,
      persist?: boolean,
      action?: ReactNode
    ) => {
      const toastIdVal = id || String(++toastId);
      setToasts((prev) => [
        ...prev,
        { id: toastIdVal, message, header, type, onClose: handleClose, action },
      ]);
      if (!persist) {
        setTimeout(() => handleClose(toastIdVal), 4000);
      }
    },
    [handleClose]
  );

  // Register global bridge when provider mounts so legacy scripts can call
  // window.Toast or dispatch `ui-show-toast` events that map to showToast.
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const cleanup = registerGlobalToastBridge(showToast);
      return cleanup;
    }
  }, [showToast]);

  return (
    <ToastContext.Provider value={{ showToast, dismissToast: handleClose }}>
      {children}
      {toasts.length > 0 && <ToastPortal toasts={toasts} onClose={handleClose} />}
    </ToastContext.Provider>
  );
};

// When a ToastProvider is mounted in the app, it will set up a global
// bridge so legacy scripts can call window.Toast(...) and dispatch
// `ui-show-toast` events to trigger the React toast system.
// We attach these handlers to the global object when this module is loaded
// inside a React tree by leveraging a small mounting helper.
// Returns a cleanup function to remove the event listener.
export function registerGlobalToastBridge(showToastFn: ToastContextType['showToast']): () => void {
  if (typeof window === 'undefined') return () => {};

  // Set window.Toast for legacy scripts expecting a function
  // Keep a lightweight wrapper to match the legacy signature: ({type,title,message})
  // but allow calling showToast(message,type,title) as well.
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  window.Toast = function (optsOrMessage: unknown, maybeType?: string, maybeTitle?: string) {
    if (typeof optsOrMessage === 'object' && optsOrMessage !== null) {
      const obj = optsOrMessage as {
        type?: string;
        title?: string;
        message?: string;
      };
      const t = (obj.type as ToastType) || 'info';
      showToastFn(obj.message || '', t, obj.title);
    } else {
      // showToast(message, type?, title?)
      showToastFn(String(optsOrMessage), (maybeType as ToastType) || 'info', maybeTitle);
    }
  };

  // Listen for custom events dispatched on document for cross-framework calls
  function onUiShowToast(e: Event) {
    const detail = (e as CustomEvent).detail || {};
    const { message = '', type = 'info', header } = detail;
    showToastFn(message, type, header);
  }

  document.addEventListener('ui-show-toast', onUiShowToast as EventListener);
  return () => document.removeEventListener('ui-show-toast', onUiShowToast as EventListener);
}

// Expose a global bridge for non-React JS that expects window.Toast and
// listen for a simple custom event `ui-show-toast` so other scripts (or
// components rendered outside of the provider) can request toasts.
// This is intentionally outside the component so the module can be imported
// without side-effects; the provider registers the bridge when mounted.

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
};
